import { ContentfulCards, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { CardGroupRendererProps } from '../CardGroup/CardGroupRenderer';
import { MiniCardGroupRendererProps } from '../MiniCardGroup/MiniCardGroupRenderer';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { getLocalTimeWithoutTimezone } from '@jbhi-fi/utils/src/date';
import { CardTransformer } from './CardTransformer';

export const toContentfulCardFilters = ({
  filter,
  fromLastXDays,
}: {
  filter: Omit<ContentfulFilter, 'content_type'>;
  fromLastXDays?: number;
}): Array<ContentfulFilter> => {
  const updatedFilter = { ...filter };

  if (fromLastXDays)
    updatedFilter['sys.updatedAt[gte]'] = new Date(Date.now() - fromLastXDays * 86400000).toISOString();

  return [
    {
      ...updatedFilter,
      content_type: EntryType.competition,
      'fields.endDate[gte]': getLocalTimeWithoutTimezone(new Date()),
      'fields.hideFromCardGrid[ne]': 'true',
    },
    { ...updatedFilter, content_type: EntryType.article, order: '-fields.publishedDate' },
    {
      ...updatedFilter,
      content_type: EntryType.genericPage,
      order: '-fields.publishedDate',
      'fields.heroLogo[exists]': 'true',
    },
  ];
};

export const toCardGridParentPageFilter = ({
  parentPage,
}: {
  parentPage?: ContentfulCards['fields']['parentPage'];
}): Omit<ContentfulFilter, 'content_type'> => {
  const filter: Omit<ContentfulFilter, 'content_type'> = { order: '-sys.createdAt' };

  switch (parentPage?.sys?.contentType?.sys?.id) {
    case EntryType.categoryLanding:
      filter['fields.parentPage.sys.contentType.sys.id'] = EntryType.subCategory;
      filter['fields.parentPage.fields.categoryPage.sys.id'] = parentPage.sys.id;
      break;
    case EntryType.subCategory:
      filter['fields.parentPage.sys.id'] = parentPage.sys.id;
      break;
  }

  return filter;
};

const getCardsLinkUrl = (entry?: ContentfulCards): string => {
  const parentPage = entry?.fields?.parentPage;
  return CardTransformer.isSubCategory(parentPage)
    ? `/blogs/${parentPage?.fields?.categoryPage?.fields?.urlSlug ?? ''}/${parentPage?.fields?.urlSlug ?? ''}`
    : `/blogs/${parentPage?.fields?.urlSlug ?? ''}`;
};

const getCardsLinkProps = (entry?: ContentfulCards) => ({
  href: getCardsLinkUrl(entry),
  label: `View all ${entry?.fields?.parentPage?.fields?.heading}`,
});

const getCardsContentfulFilters = (entry?: ContentfulCards) =>
  entry?.fields?.parentPage?.sys?.id
    ? toContentfulCardFilters({
        filter: toCardGridParentPageFilter({ parentPage: entry.fields.parentPage }),
      })
    : [];

export abstract class CardGroupTransformer {
  static toCardGroupProps = (entry?: ContentfulCards): CardGroupRendererProps => {
    return {
      heading: entry?.fields?.heading || '',
      linkProps: getCardsLinkProps(entry),
      cards: entry?.fields?.cards?.map(cardEntry => CardTransformer.toCardProps(cardEntry)) || [],
      contentfulFilters: getCardsContentfulFilters(entry),
      desktopRows: entry?.fields?.desktopRows,
    };
  };

  static toMiniCardGroupProps = (entry?: ContentfulCards): MiniCardGroupRendererProps => {
    return {
      heading: entry?.fields?.heading || '',
      linkProps: getCardsLinkProps(entry),
      cards: entry?.fields?.cards?.map(cardEntry => CardTransformer.toMiniCardProps(cardEntry)).filter(Boolean) || [],
      contentfulFilters: getCardsContentfulFilters(entry),
    };
  };
}
